import type { RouteLocationNormalized } from "vue-router";
import { AppRouteNames } from "@/shared/@types/AppRouteNames";
import { FeatureFlag } from "@/shared/@types/FeatureFlag";
import { WebStoreId } from "@/shared/@types/WebStoreId";
import {
  isWebStoreWithGasMixtureConfigurator,
  isWebStoreWithPalletConfigurator,
  isWebStoreWithRGoodsAndSystemBottles,
} from "@/shared/helper/webStorePermission.helper";
import { useFeatureFlagsStore } from "@/shared/stores/FeatureFlagsStore";
import { isNotProduction } from "@/shared/helper/environment.helper";

const StartView = () =>
  import("@/app/gas-shop-app/start/StartView/StartView.vue");
const FavoritesView = () =>
  import("@/app/gas-shop-app/favorites/FavoritesView/FavoritesView.vue");
const QuickOrderView = () =>
  import("@/app/gas-shop-app/quick-order/QuickOrderView/QuickOrderView.vue");
const OrdersView = () =>
  import("@/app/gas-shop-app/orders/OrdersView/OrdersView.vue");
const DocumentsView = () =>
  import("@/app/gas-shop-app/documents/DocumentsView/DocumentsView.vue");
const BottleInventoryView = () =>
  import("@/app/gas-shop-app/bottle-inventory/BottleInventoryView/BottleInventoryView.vue");
const ReturnEmptiesView = () =>
  import("@/app/gas-shop-app/return/empties/EmptiesView/EmptiesView.vue");
const ReturnComplaintsView = () =>
  import("@/app/gas-shop-app/return/complaints/ComplaintsView/ComplaintsView.vue");
const RGoodsAndSystemBottlesView = () =>
  import("@/app/gas-shop-app/refrigerant-management/r-goods-and-system-bottles/RGoodsAndSystemBottlesView/RGoodsAndSystemBottlesView.vue");
const QuotaManagementView = () =>
  import("@/app/gas-shop-app/refrigerant-management/quota-management/QuotaManagementView/QuotaManagementView.vue");
const ShippingAddressesView = () =>
  import("@/app/gas-shop-app/shipping-addresses/ShippingAddressesView/ShippingAddressesView.vue");
const EmailAddressesView = () =>
  import("@/app/gas-shop-app/email-addresses/EmailAddressesView/EmailAddressesView.vue");
const PalletConfiguratorView = () =>
  import("@/app/gas-shop-app/pallet-configurator/PalletConfiguratorView/PalletConfiguratorView.vue");
const GasMixtureConfiguratorView = () =>
  import("@/app/gas-shop-app/gas-mixture-configurator/GasMixtureConfiguratorView/GasMixtureConfiguratorView.vue");
const ProductCatalogView = () =>
  import("@/app/gas-shop-app/product-catalog/ProductCatalogView/ProductCatalogView.vue");
const ProductDetailsView = () =>
  import("@/app/gas-shop-app/product-catalog/product-details/ProductDetailsView/ProductDetailsView.vue");
const ShoppingCartView = () =>
  import("@/app/gas-shop-app/shopping-cart/ShoppingCartView/ShoppingCartView.vue");
const CheckoutView = () =>
  import("@/app/gas-shop-app/checkout/CheckoutView/CheckoutView.vue");
const AddCustomerView = () =>
  import("@/app/gas-shop-app/add-customer/AddCustomerView/AddCustomerView.vue");
const PageNotFoundView = () =>
  import("@/shared/views/PageNotFoundView/PageNotFoundView.vue");

export const routes = [
  {
    path: ":webStoreId",
    redirect: { name: AppRouteNames.GasShopStart },
    children: [
      {
        path: "start",
        name: AppRouteNames.GasShopStart,
        meta: {
          titleKey: "gasShop.start.title",
        },
        component: StartView,
      },
      {
        path: "favorites",
        name: AppRouteNames.GasShopFavorites,
        meta: {
          titleKey: "gasShop.favorites.title",
        },
        component: FavoritesView,
      },
      {
        path: "quick-order",
        name: AppRouteNames.GasShopQuickOrder,
        meta: {
          titleKey: "gasShop.quickOrder.title",
        },
        component: QuickOrderView,
      },
      {
        path: "orders",
        name: AppRouteNames.GasShopOpenOrders,
        meta: {
          titleKey: "gasShop.orders.title",
        },
        component: OrdersView,
      },
      {
        path: "documents",
        name: AppRouteNames.GasShopDocuments,
        meta: {
          titleKey: "shared.documents.title",
        },
        component: DocumentsView,
        beforeEnter: () =>
          useFeatureFlagsStore().isFeatureEnabled(
            FeatureFlag.GasShopAccessDocuments,
          )
            ? true
            : AppRouteNames.GasShopNotFound,
      },
      {
        path: "bottle-inventory",
        name: AppRouteNames.GasShopBottleInventory,
        meta: {
          titleKey: "gasShop.bottleInventory.title",
        },
        component: BottleInventoryView,
      },
      {
        path: "gas-mixture-configurator",
        name: AppRouteNames.GasShopGasMixtureConfigurator,
        meta: {
          titleKey: "gasShop.gasMixtureConfigurator.title",
        },
        component: GasMixtureConfiguratorView,
        beforeEnter: (to: RouteLocationNormalized) => {
          // TODO: Remove isNotProduction when the feature is ready for production
          if (
            !isNotProduction() ||
            !isWebStoreWithGasMixtureConfigurator(
              to.params.webStoreId as WebStoreId,
            )
          ) {
            return {
              name: AppRouteNames.GasShopStart,
              params: { webStoreId: to.params.webStoreId },
            };
          }
          return true;
        },
      },
      {
        path: "return",
        redirect: { name: AppRouteNames.GasShopReturnEmpties },
        children: [
          {
            path: "empties",
            name: AppRouteNames.GasShopReturnEmpties,
            meta: {
              titleKey: "gasShop.return.empties.title",
            },
            component: ReturnEmptiesView,
          },
          {
            path: "complaints",
            name: AppRouteNames.GasShopReturnComplaints,
            meta: {
              titleKey: "gasShop.return.complaints.title",
            },
            component: ReturnComplaintsView,
          },
        ],
      },

      {
        path: "refrigerant-management",
        redirect: { name: AppRouteNames.GasShopQuotaManagement },
        children: [
          {
            path: "r-goods-and-system-bottles",
            name: AppRouteNames.GasShopRGoodsAndSystemBottles,
            meta: {
              titleKey:
                "gasShop.refrigerantManagement.rGoodsAndSystemBottles.title",
            },
            component: RGoodsAndSystemBottlesView,
            beforeEnter: (to: RouteLocationNormalized) => {
              if (
                !isWebStoreWithRGoodsAndSystemBottles(
                  to.params.webStoreId as WebStoreId,
                )
              ) {
                return {
                  name: AppRouteNames.GasShopStart,
                  params: { webStoreId: to.params.webStoreId },
                };
              }
              return true;
            },
          },
          {
            path: "quota-management",
            name: AppRouteNames.GasShopQuotaManagement,
            meta: {
              titleKey: "gasShop.refrigerantManagement.quotaManagement.title",
            },
            component: QuotaManagementView,
          },
        ],
      },
      {
        path: "delivery-addresses",
        name: AppRouteNames.GasShopShippingAddresses,
        meta: {
          titleKey: "gasShop.shippingAddresses.title",
        },
        component: ShippingAddressesView,
      },
      {
        path: "email-addresses",
        name: AppRouteNames.GasShopEmailAddresses,
        meta: {
          titleKey: "gasShop.emailAddresses.title",
        },
        component: EmailAddressesView,
      },
      {
        path: "pallet-configurator",
        name: AppRouteNames.GasShopPalletConfigurator,
        meta: {
          titleKey: "gasShop.palletConfigurator.title",
        },
        component: PalletConfiguratorView,
        beforeEnter: (to: RouteLocationNormalized) => {
          if (
            !isWebStoreWithPalletConfigurator(
              to.params.webStoreId as WebStoreId,
            )
          ) {
            return {
              name: AppRouteNames.GasShopStart,
              params: { webStoreId: to.params.webStoreId },
            };
          }
          return true;
        },
      },
      {
        path: "product-catalog/:category",
        name: AppRouteNames.GasShopProductCatalogCategory,
        meta: {
          titleKey: "gasShop.product.catalog.title",
        },
        component: ProductCatalogView,
        props: true,
      },
      {
        path: "product-catalog/details/:productExternalId/:productVariantExternalId?",
        name: AppRouteNames.GasShopProductCatalogProductDetails,
        meta: {
          titleKey: "gasShop.product.catalog.title",
        },
        component: ProductDetailsView,
        props: true,
      },
      {
        path: "shopping-cart",
        name: AppRouteNames.GasShopShoppingCart,
        meta: {
          titleKey: "gasShop.shoppingCart.title",
        },
        component: ShoppingCartView,
      },
      {
        path: "checkout",
        name: AppRouteNames.GasShopCheckout,
        meta: {
          titleKey: "gasShop.checkout.title",
        },
        component: CheckoutView,
      },
    ],
  },
  {
    path: "add-customer",
    name: AppRouteNames.GasShopAddCustomer,
    meta: {
      titleKey: "shared.addCustomer.title",
    },
    component: AddCustomerView,
  },
  {
    path: ":pathMatch(.*)*",
    name: AppRouteNames.GasShopNotFound,
    meta: {
      titleKey: "shared.error.page.notFoundError.title",
    },
    component: PageNotFoundView,
    props: {
      homeRouteName: AppRouteNames.GasShopStart,
    },
  },
];
